import React, { useState } from 'react';
import { scraperService } from '../services/scraperService';

interface WebScraperProps {
  onSummaryGenerated: (summary: string, tenantCode: string) => void;
}

const WebScraper: React.FC<WebScraperProps> = ({ onSummaryGenerated }) => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [summary, setSummary] = useState('');
  const [error, setError] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const validateUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const scrapeWebsite = async () => {
    if (!url.trim()) {
      setError('Please enter a website URL');
      return;
    }

    if (!validateUrl(url)) {
      setError('Please enter a valid URL (e.g., https://example.com)');
      return;
    }

    setIsLoading(true);
    setError('');
    setSummary('');

    try {
      // Scrape website and generate AI summary in one call
      const aiSummary = await scraperService.scrapeAndSummarize(url);
      setSummary(aiSummary);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to scrape website. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };



  const downloadSummary = () => {
    const blob = new Blob([summary], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'website-summary.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const uploadAsTenant = async () => {
    setIsUploading(true);
    try {
      const urlObj = new URL(url);
      const tenantCode = urlObj.hostname.replace('www.', '');
      await onSummaryGenerated(summary, tenantCode);
      setSummary('');
      setUrl('');
    } catch (err) {
      setError('Invalid URL format');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div>
      <div className="form-group">
        <label htmlFor="website-url">Website URL</label>
        <input
          id="website-url"
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          disabled={isLoading}
        />
        {error && <div className="error-text">{error}</div>}
      </div>

      <button 
        onClick={scrapeWebsite} 
        className="btn" 
        disabled={isLoading}
        style={{width: '100%', marginBottom: '20px'}}
      >
        {isLoading ? 'Scraping & Analyzing...' : 'Scrape & Analyze Website'}
      </button>

      {summary && (
        <div style={{marginTop: '20px'}}>
          <h3 style={{color: 'var(--primary)', marginBottom: '15px'}}>Summary Generated</h3>
          <div style={{
            background: '#f8f9fa', 
            padding: '15px', 
            borderRadius: '8px', 
            maxHeight: '300px', 
            overflow: 'auto',
            marginBottom: '15px',
            fontSize: '0.9rem',
            lineHeight: '1.6',
            border: '1px solid #e9ecef'
          }}>
            <div dangerouslySetInnerHTML={{
              __html: summary
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/\*(.*?)\*/g, '<em>$1</em>')
                .split('\n\n')
                .map(paragraph => `<p style="margin-bottom: 12px;">${paragraph.replace(/\n/g, '<br/>')}</p>`)
                .join('')
            }} />
          </div>
          
          <div style={{display: 'flex', gap: '10px'}}>
            <button 
              onClick={downloadSummary}
              className="btn"
              style={{flex: 1, background: 'var(--secondary)'}}
            >
              Download Summary
            </button>
            <button 
              onClick={uploadAsTenant}
              className="btn"
              style={{flex: 1}}
              disabled={isUploading}
            >
              {isUploading ? 'Uploading...' : 'Upload as Tenant'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebScraper;